// Map thumbnails for vote cards and Practice (§14.2 "map thumbnail rendered via a small offscreen
// render at boot"): a Canvas 2D top-down diorama of the map's real walkable ground (`groundAt` at
// round start) as a thick floating slab in the §12.2 palette, with its solids (cup, bumpers,
// sweeper arms, candles) and a per-map surface motif, over the map's sky gradient. Drawn once per
// map on first use (or during idle time after boot) and cached as a data URL.
import { MAX_BEANS, NO_SLOT, VOID } from '@shared/constants';
import { ItemKind, MapId, ModeId, MutatorId } from '@shared/ids';
import { MAPS } from '@shared/maps/index';
import { createSolid, type Solid } from '@shared/maps/mapTypes';
import { createWorld } from '@shared/sim/world';
import type { RoundConfig } from '@shared/types';
import { MAP_PALETTES } from '../render/maps/palettes';
import { el } from './ui';

const W = 360;
const H = 220;
const INK: readonly [number, number, number] = [42, 26, 58];
const SLAB = 10;
const cache = new Map<MapId, string>();

function roundConfig(mapId: MapId): RoundConfig {
  return {
    mapId, modeId: mapId === MapId.LOBBY ? ModeId.LOBBY : ModeId.ROYALE, mutator: MutatorId.NONE, seed: 7, golden: false, itemsOn: false, teamsOn: false,
    activeSlots: new Array<boolean>(MAX_BEANS).fill(false), teams: new Array<number>(MAX_BEANS).fill(0),
    matchScores: new Array<number>(MAX_BEANS).fill(0), crownedSlot: NO_SLOT, startItems: new Array<ItemKind>(MAX_BEANS).fill(ItemKind.NONE),
  };
}

function rgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function darken(c: readonly [number, number, number], k: number): [number, number, number] {
  return [Math.round(c[0] * k), Math.round(c[1] * k), Math.round(c[2] * k)];
}

/** Small deterministic PRNG for decorations. */
function prng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Square dilation by `r` cells (two separable passes). */
function dilate(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
  const tmp = new Uint8Array(mask.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let v = 0;
      for (let i = Math.max(0, x - r); i <= Math.min(w - 1, x + r) && !v; i++) v = mask[y * w + i];
      tmp[y * w + x] = v;
    }
  }
  const out = new Uint8Array(mask.length);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let v = 0;
      for (let j = Math.max(0, y - r); j <= Math.min(h - 1, y + r) && !v; j++) v = tmp[j * w + x];
      out[y * w + x] = v;
    }
  }
  return out;
}

/** Pixels within `depth` rows below a set pixel of `mask` (the slab sides). */
function below(mask: Uint8Array, w: number, h: number, depth: number): Uint8Array {
  const out = new Uint8Array(mask.length);
  for (let x = 0; x < w; x++) {
    let last = -1e9;
    for (let y = 0; y < h; y++) {
      if (mask[y * w + x]) last = y;
      else if (y - last <= depth) out[y * w + x] = 1;
    }
  }
  return out;
}

/** Draws the thumbnail of `mapId` into a new canvas. */
export function drawMapThumb(mapId: MapId, w = W, h = H): HTMLCanvasElement {
  const def = MAPS[mapId];
  const pal = MAP_PALETTES[mapId];
  const world = createWorld(roundConfig(mapId), 0, false);
  const canvas = el('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const rng = prng(mapId * 977 + 13);

  // Sky with soft bokeh.
  const sky = ctx.createLinearGradient(0, 0, 0, h);
  sky.addColorStop(0, pal.skyTop);
  sky.addColorStop(1, pal.skyBottom);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
  for (let i = 0; i < 14; i++) {
    ctx.beginPath();
    ctx.arc(rng() * w, rng() * h, 3 + rng() * 10, 0, Math.PI * 2);
    ctx.fill();
  }

  // Fit the bounds into the card, leaving room under the slab.
  const b = def.bounds;
  const bw = b.maxX - b.minX;
  const bh = b.maxY - b.minY;
  const scale = Math.min((w - 40) / bw, (h - 40 - SLAB) / bh);
  const ox = (w - bw * scale) / 2;
  const oy = (h - SLAB - bh * scale) / 2;
  const toX = (x: number): number => ox + (x - b.minX) * scale;
  const toY = (y: number): number => oy + (y - b.minY) * scale;

  // Ground coverage at pixel resolution.
  const mask = new Uint8Array(w * h);
  for (let py = 0; py < h; py++) {
    const y = b.minY + (py + 0.5 - oy) / scale;
    if (y < b.minY - 1 || y > b.maxY + 1) continue;
    for (let px = 0; px < w; px++) {
      const x = b.minX + (px + 0.5 - ox) / scale;
      if (x < b.minX - 1 || x > b.maxX + 1) continue;
      if (def.groundAt(world, x, y) > VOID / 2) mask[py * w + px] = 1;
    }
  }
  const outline = dilate(mask, w, h, 2);
  const shadowSrc = dilate(mask, w, h, 5);
  const sideFill = below(mask, w, h, SLAB - 1);
  const sideInk = below(outline, w, h, SLAB);

  const base = rgb(pal.base);
  const side = mapId === MapId.PINBALL ? rgb('#120B2E') : darken(base, 0.72);
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const sdx = 7;
  const sdy = SLAB + 12;
  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      const i = py * w + px;
      const k = i * 4;
      let c: readonly [number, number, number] | null = null;
      if (mask[i]) c = base;
      else if (outline[i]) c = INK;
      else if (sideFill[i]) c = side;
      else if (sideInk[i]) c = INK;
      if (c) {
        d[k] = c[0];
        d[k + 1] = c[1];
        d[k + 2] = c[2];
        continue;
      }
      const sx = px - sdx;
      const sy = py - sdy;
      if (sx >= 0 && sy >= 0 && shadowSrc[sy * w + sx]) {
        d[k] = Math.round(d[k] * 0.8 + INK[0] * 0.2);
        d[k + 1] = Math.round(d[k + 1] * 0.8 + INK[1] * 0.2);
        d[k + 2] = Math.round(d[k + 2] * 0.8 + INK[2] * 0.2);
      }
    }
  }
  ctx.putImageData(img, 0, 0);

  // Surface motif on its own canvas, masked to the top face.
  const motif = el('canvas');
  motif.width = w;
  motif.height = h;
  const mctx = motif.getContext('2d');
  const maskCanvas = el('canvas');
  maskCanvas.width = w;
  maskCanvas.height = h;
  const kctx = maskCanvas.getContext('2d');
  if (mctx && kctx) {
    drawMotif(mctx, mapId, pal, w, h, toX, toY, scale, rng);
    const mimg = kctx.createImageData(w, h);
    for (let i = 0; i < mask.length; i++) mimg.data[i * 4 + 3] = mask[i] ? 255 : 0;
    kctx.putImageData(mimg, 0, 0);
    mctx.globalCompositeOperation = 'destination-in';
    mctx.drawImage(maskCanvas, 0, 0);
    ctx.drawImage(motif, 0, 0);
  }
  // A glossy highlight band across the top face.
  ctx.save();
  const gloss = ctx.createLinearGradient(0, oy, 0, oy + bh * scale);
  gloss.addColorStop(0, 'rgba(255, 255, 255, 0.28)');
  gloss.addColorStop(0.45, 'rgba(255, 255, 255, 0)');
  if (mctx) {
    mctx.globalCompositeOperation = 'source-over';
    mctx.clearRect(0, 0, w, h);
    mctx.fillStyle = gloss;
    mctx.fillRect(0, 0, w, h);
    mctx.globalCompositeOperation = 'destination-in';
    mctx.drawImage(maskCanvas, 0, 0);
    ctx.drawImage(motif, 0, 0);
  }
  ctx.restore();

  // Solids at round start (cup, bumpers, flippers, sweeper arms, candles, hub).
  const solids: Solid[] = Array.from({ length: 64 }, createSolid);
  const count = def.solids(world, solids);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  for (let i = 0; i < count; i++) {
    const s = solids[i];
    const fill = solidColor(mapId, s.tag);
    if (s.kind === 0) {
      const r = Math.max(3, s.r * scale);
      const x = toX(s.ax);
      const y = toY(s.ay) - 3;
      ctx.fillStyle = 'rgba(42, 26, 58, 0.25)';
      ctx.beginPath();
      ctx.arc(x + 2, y + 5, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = fill;
      ctx.strokeStyle = '#2A1A3A';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.beginPath();
      ctx.arc(x - r * 0.32, y - r * 0.32, Math.max(1, r * 0.28), 0, Math.PI * 2);
      ctx.fill();
    } else {
      const lw = Math.max(3, s.r * 2 * scale);
      ctx.strokeStyle = '#2A1A3A';
      ctx.lineWidth = lw + 4;
      ctx.beginPath();
      ctx.moveTo(toX(s.ax), toY(s.ay) - 3);
      ctx.lineTo(toX(s.bx), toY(s.by) - 3);
      ctx.stroke();
      ctx.strokeStyle = fill;
      ctx.lineWidth = lw;
      ctx.stroke();
    }
  }
  return canvas;
}

function solidColor(mapId: MapId, tag: number): string {
  switch (mapId) {
    case MapId.PINBALL: return tag === 1 ? '#FF3EC8' : tag === 4 ? '#E8EEF4' : '#2FF3FF';
    case MapId.SPIN: return tag === 3 ? '#9AA9B8' : '#4FD1C5';
    case MapId.TEACUP: return '#FFFFFF';
    case MapId.CAKE: return '#FFF1E0';
    default: return '#FF5A4E';
  }
}

function drawMotif(
  ctx: CanvasRenderingContext2D, mapId: MapId, pal: { accent1: string; accent2: string; base: string },
  w: number, h: number, toX: (x: number) => number, toY: (y: number) => number, scale: number, rng: () => number,
): void {
  ctx.lineCap = 'round';
  switch (mapId) {
    case MapId.TEACUP: {
      // Porcelain glaze rings in mint and a gold band.
      ctx.strokeStyle = pal.accent1;
      ctx.lineWidth = 3;
      for (const r of [3.4, 5.4]) {
        ctx.beginPath();
        ctx.arc(toX(0), toY(0), r * scale, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.strokeStyle = pal.accent2;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(toX(0), toY(0), 7.3 * scale, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case MapId.PINBALL: {
      ctx.strokeStyle = 'rgba(122, 60, 255, 0.6)';
      ctx.lineWidth = 1;
      for (let x = 0; x < w; x += 12) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += 12) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
      ctx.fillStyle = pal.accent1;
      for (let i = 0; i < 4; i++) {
        const y = toY(-3 + i * 2.2);
        for (const x of [w / 2 - 40, w / 2 + 40]) {
          ctx.beginPath();
          ctx.moveTo(x - 6, y + 5);
          ctx.lineTo(x, y - 3);
          ctx.lineTo(x + 6, y + 5);
          ctx.closePath();
          ctx.fill();
        }
      }
      break;
    }
    case MapId.CAKE: {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.lineWidth = 1.6;
      const r = 1.04 * scale;
      for (let q = -5; q <= 5; q++) {
        for (let s = -5; s <= 5; s++) {
          const cx = toX(1.8 * (q + s / 2));
          const cy = toY(1.56 * s);
          ctx.beginPath();
          for (let k = 0; k <= 6; k++) {
            const ang = Math.PI / 6 + (k * Math.PI) / 3;
            const x = cx + Math.cos(ang) * r;
            const y = cy + Math.sin(ang) * r;
            if (k === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
      }
      const sprinkles = ['#FF5A4E', '#FFC93C', '#3DDC97', '#4CB8FF', '#9B6BFF'];
      ctx.lineWidth = 2.6;
      for (let i = 0; i < 180; i++) {
        const x = rng() * w;
        const y = rng() * h;
        const a = rng() * Math.PI;
        ctx.strokeStyle = sprinkles[i % sprinkles.length];
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + Math.cos(a) * 4, y + Math.sin(a) * 4);
        ctx.stroke();
      }
      break;
    }
    case MapId.SPIN: {
      ctx.fillStyle = 'rgba(154, 169, 184, 0.6)';
      for (let y = 0; y < h; y += 9) {
        for (let x = (y / 9) % 2 ? 4.5 : 0; x < w; x += 9) {
          ctx.beginPath();
          ctx.arc(x, y, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.strokeStyle = pal.accent1;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(toX(0), toY(0), 6.9 * scale, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    default: {
      // Cardboard with a printed star pattern.
      ctx.fillStyle = pal.accent1;
      ctx.globalAlpha = 0.65;
      for (let y = 8; y < h; y += 22) {
        for (let x = (y / 22) % 2 ? 18 : 6; x < w; x += 24) star(ctx, x, y, 4.5);
      }
      ctx.globalAlpha = 1;
    }
  }
}

function star(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 ? r * 0.45 : r;
    if (i === 0) ctx.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    else ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fill();
}

/** The cached thumbnail data URL of a map (drawn on first use). */
export function mapThumbUrl(mapId: MapId): string {
  let url = cache.get(mapId);
  if (url === undefined) {
    try {
      url = drawMapThumb(mapId).toDataURL('image/png');
    } catch {
      url = '';
    }
    cache.set(mapId, url);
  }
  return url;
}

/** An <img> of the map thumbnail. */
export function mapThumb(mapId: MapId, cls = 'map-thumb'): HTMLImageElement {
  return el('img', { class: cls, attrs: { src: mapThumbUrl(mapId), alt: '', draggable: 'false' } });
}

/** Pre-draws thumbnails during idle time after boot. */
export function warmMapThumbs(ids: readonly MapId[]): void {
  let i = 0;
  const next = (): void => {
    if (i >= ids.length) return;
    mapThumbUrl(ids[i++]);
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    if (ric) ric(next);
    else setTimeout(next, 50);
  };
  setTimeout(next, 400);
}
